<template>
  <div
    class="sidenav"
    :class="{ 'sidenav-collapsed': isCollapsed, 'sidenav-drawer': isDrawerMode }"
    v-bind="$attrs"
  >
    <div v-if="isCollapsed" class="collapsed-logo-top">
      <router-link :to="{ name: DASHBOARD_ROUTE }">
        <img
          src="../../assets/images/icons/sidenav/logo-icon.png"
          class="logo-icon-small"
          alt="Logo"
        />
      </router-link>
    </div>

    <br v-if="!isCollapsed" />
    <div class="links">
      <ul class="">
        <div class="row" v-if="!isCollapsed">
          <div class="col-lg-12 col-md-12">
            <router-link :to="{ name: DASHBOARD_ROUTE }" class="logo-link">
              <div class="logo-container">
                <img src="../../assets/images/logo.png" class="logo-img" alt="SalesBuckets" />
              </div>
            </router-link>
          </div>
        </div>
        <br />

        <div v-if="isCollapsed" class="collapsed-nav-icons">
          <router-link :to="{ name: DASHBOARD_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: route === DASHBOARD_ROUTE }">
              <Icon icon="lucide:layout-dashboard" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Dashboard</div>
            </div>
          </router-link>
          <router-link :to="{ name: CONTACTS_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: isContactsRouteActive }">
              <Icon icon="lucide:users" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Contacts</div>
            </div>
          </router-link>
          <router-link :to="{ name: COMPANIES_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: isCompaniesRouteActive }">
              <Icon icon="lucide:building-2" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Accounts</div>
            </div>
          </router-link>

          <div
            class="collapsed-nav-item-container"
            @mouseenter="showBucketsTooltip = true"
            @mouseleave="showBucketsTooltip = false"
          >
            <router-link :to="{ name: BUCKETS_LIST_ROUTE }">
              <div class="collapsed-nav-item" :class="{ selected: isBucketsRouteActive }">
                <Icon icon="fa6-solid:bucket" width="20" height="20" />
                <span
                  v-if="totalBucketUnreadCount > 0"
                  class="sidenav-unread-badge sidenav-unread-badge--collapsed"
                  :title="`${totalBucketUnreadCount} unread message${totalBucketUnreadCount === 1 ? '' : 's'} across all buckets`"
                >
                  {{ totalBucketUnreadCount > 99 ? "99+" : totalBucketUnreadCount }}
                </span>
              </div>
            </router-link>
            <div
              v-if="showBucketsTooltip"
              class="nav-tooltip"
              @mouseenter="showBucketsTooltip = true"
              @mouseleave="showBucketsTooltip = false"
            >
              <div class="tooltip-content">
                <div class="tooltip-items">
                  <div v-if="!bucketOptions || bucketOptions.length === 0" class="tooltip-empty">
                    No buckets yet
                  </div>
                  <draggable
                    v-else
                    v-model="bucketOptionEditable"
                    item-key="slug"
                    tag="div"
                    ghost-class="tooltip-dragging"
                    animation="200"
                    @change="debouncedDragBucketOption"
                  >
                    <template #item="{ element: bucketOption }">
                      <router-link
                        :key="bucketOption.slug"
                        :to="{
                          name: BUCKETS_ROUTE,
                          params: { bucket: bucketOption.slug },
                          query: { status: 'due' },
                        }"
                        class="tooltip-item"
                        :class="{
                          'tooltip-item-selected': routeParams.bucket === bucketOption.slug,
                        }"
                      >
                        <Icon
                          icon="lucide:grip-vertical"
                          class="tooltip-icon tooltip-drag-handle"
                        />
                        <Icon
                          icon="fa6-solid:bucket"
                          class="tooltip-icon tooltip-item__bucket-icon"
                          :style="{ color: bucketOption.colorHex }"
                        />
                        <span>{{ bucketOption.name }}</span>
                        <Icon
                          v-if="isBucketOptionLocked(bucketOption.slug)"
                          icon="lucide:lock"
                          class="sidenav-lock-icon"
                        />
                        <span
                          v-if="bucketOption.count > 0"
                          class="sidenav-due-icon"
                          :class="{
                            glowing: bucketOption.count > 0,
                            pulsing: bucketOption.count > 0,
                          }"
                          :title="`${bucketOption.count} tasks due today`"
                        >
                          <Icon icon="lucide:calendar-clock" class="sidenav-due-icon__icon" />
                          <span class="sidenav-due-icon__count">{{ bucketOption.count }}</span>
                        </span>
                        <span
                          v-if="bucketOption.unreadCount > 0"
                          class="sidenav-unread-badge sidenav-unread-badge--collapsed"
                          :title="`${bucketOption.unreadCount} unread message${bucketOption.unreadCount === 1 ? '' : 's'}`"
                        >
                          {{ bucketOption.unreadCount > 99 ? "99+" : bucketOption.unreadCount }}
                        </span>
                      </router-link>
                    </template>
                  </draggable>
                </div>
                <button
                  type="button"
                  class="tooltip-footer-action"
                  @click.stop="showAddBucketOptionModal = true"
                >
                  <Icon icon="lucide:plus" width="14" height="14" />
                  <span>New bucket</span>
                </button>
              </div>
            </div>
          </div>
          <router-link :to="{ name: MEETINGS_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: route === MEETINGS_ROUTE }">
              <Icon icon="lucide:calendar" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Meetings</div>
            </div>
          </router-link>

          <router-link :to="{ name: OPPORTUNITIES_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: isOpportunitiesRouteActive }">
              <Icon icon="lucide:trophy" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Opportunity</div>
            </div>
          </router-link>

          <div
            class="collapsed-nav-item-container"
            @mouseenter="showReportsTooltip = true"
            @mouseleave="showReportsTooltip = false"
          >
            <div class="collapsed-nav-item" :class="{ selected: isReportsRouteActive }">
              <Icon icon="lucide:bar-chart-3" width="20" height="20" />
            </div>
            <div
              v-if="showReportsTooltip"
              class="nav-tooltip"
              @mouseenter="showReportsTooltip = true"
              @mouseleave="showReportsTooltip = false"
            >
              <div class="tooltip-content">
                <div class="tooltip-items">
                  <router-link
                    :to="{ name: ACTIVITY_REPORTS_ROUTE }"
                    class="tooltip-item"
                    :class="{ 'tooltip-item-selected': route === ACTIVITY_REPORTS_ROUTE }"
                  >
                    <Icon icon="lucide:activity" class="tooltip-icon" width="14" height="14" />
                    <span>Activity Report</span>
                  </router-link>
                  <router-link
                    :to="{ name: REPORTS_ROUTE }"
                    class="tooltip-item"
                    :class="{ 'tooltip-item-selected': route === REPORTS_ROUTE }"
                  >
                    <Icon icon="lucide:trending-up" class="tooltip-icon" width="14" height="14" />
                    <span>Metrics</span>
                  </router-link>
                  <router-link
                    :to="{ name: OPPORTUNITIES_REPORTS_ROUTE }"
                    class="tooltip-item"
                    :class="{ 'tooltip-item-selected': route === OPPORTUNITIES_REPORTS_ROUTE }"
                  >
                    <Icon icon="lucide:trophy" class="tooltip-icon" width="14" height="14" />
                    <span>Opportunity Report</span>
                  </router-link>
                </div>
              </div>
            </div>
          </div>
          <router-link
            :to="{ name: CALL_HISTORY_REPORTS_ROUTE }"
            class="collapsed-nav-item-container"
          >
            <div
              class="collapsed-nav-item"
              :class="{ selected: route === CALL_HISTORY_REPORTS_ROUTE }"
            >
              <Icon icon="lucide:phone" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Call History</div>
            </div>
          </router-link>
          <router-link :to="{ name: TEMPLATE_FOLDERS_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: isTemplatesRouteActive }">
              <Icon icon="lucide:file-text" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Templates</div>
            </div>
          </router-link>
          <router-link :to="{ name: SEQUENCES_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: isSequencesRouteActive }">
              <Icon icon="lucide:send" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Sequences</div>
            </div>
          </router-link>

          <router-link :to="{ name: SETTINGS_PROFILE_ROUTE }" class="collapsed-nav-item-container">
            <div class="collapsed-nav-item" :class="{ selected: isSettingsRouteActive }">
              <Icon icon="lucide:settings" width="20" height="20" />
            </div>
            <div class="nav-tooltip nav-tooltip--label">
              <div class="tooltip-content tooltip-content--label">Settings</div>
            </div>
          </router-link>
        </div>

        <div v-if="!isCollapsed">
          <router-link :to="{ name: DASHBOARD_ROUTE }">
            <li :class="[route === DASHBOARD_ROUTE ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:layout-dashboard" width="20" height="20" />
                <span class="sidenav-label">Dashboard</span>
              </span>
            </li>
          </router-link>
          <router-link :to="{ name: CONTACTS_ROUTE }">
            <li :class="[isContactsRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:users" width="20" height="20" />
                <span class="sidenav-label">Contacts</span>
              </span>
            </li>
          </router-link>
          <router-link :to="{ name: COMPANIES_ROUTE }">
            <li :class="[isCompaniesRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:building-2" width="20" height="20" />
                <span class="sidenav-label">Accounts</span>
              </span>
            </li>
          </router-link>
          <a @click="toggleBucketsMenu" class="buckets-menu-toggle">
            <li :class="[isBucketsRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="fa6-solid:bucket" width="20" height="20" />
                <span class="sidenav-label">Buckets</span>
                <span
                  v-if="totalBucketUnreadCount > 0 && !isBucketsSubmenuExpanded"
                  class="sidenav-unread-icon-wrapper"
                  :title="`${totalBucketUnreadCount} unread message${totalBucketUnreadCount === 1 ? '' : 's'} across all buckets`"
                >
                  <Icon icon="mdi:comment-text" class="sidenav-unread-icon" />
                  <span class="sidenav-unread-count-badge">
                    {{ totalBucketUnreadCount > 99 ? "99+" : totalBucketUnreadCount }}
                  </span>
                </span>
                <span class="buckets-row-actions">
                  <img
                    @click.stop.prevent="showAddBucketOptionModal = true"
                    src="../../assets/images/icons/white-plus-box.png"
                    class="add-bucket-icon"
                    alt=""
                    title="Add New Bucket"
                  />
                  <Icon
                    :icon="
                      isBucketsMenuOpen || isBucketsRouteActive
                        ? 'lucide:chevron-down'
                        : 'lucide:chevron-right'
                    "
                    class="menu-chevron buckets-chevron"
                  />
                </span>
              </span>
            </li>
          </a>
          <transition name="slide">
            <ul v-if="isBucketsSubmenuExpanded" class="sub-menu scrollable-sub-menu">
              <draggable
                v-model="bucketOptionEditable"
                item-key="slug"
                tag="ul"
                handle=".drag-handle"
                ghost-class="dragging"
                animation="200"
                @change="debouncedDragBucketOption"
              >
                <template #item="{ element: bucketOption }">
                  <router-link
                    :key="bucketOption.slug"
                    :to="{
                      name: BUCKETS_ROUTE,
                      params: { bucket: bucketOption.slug },
                      query: { status: 'due' },
                    }"
                  >
                    <li
                      :ref="(el) => setBucketRef(el, bucketOption.slug)"
                      :class="[
                        route === BUCKETS_ROUTE && routeParams.bucket === bucketOption.slug
                          ? 'selected'
                          : null,
                        'nav-items',
                      ]"
                    >
                      <span class="router-link drag-handle">
                        <Icon icon="lucide:grip-vertical" class="sub-menu-icon" />
                        <Icon
                          icon="fa6-solid:bucket"
                          class="sub-menu-icon"
                          :style="{ color: bucketOption.colorHex }"
                        />
                        <span class="sidenav-label sub-menu-text">{{ bucketOption.name }}</span>
                        <Icon
                          v-if="isBucketOptionLocked(bucketOption.slug)"
                          icon="lucide:lock"
                          class="sidenav-lock-icon"
                        />
                        <span
                          v-if="bucketOption.count > 0"
                          class="sidenav-due-icon"
                          :class="{
                            glowing: bucketOption.count > 0,
                            pulsing: bucketOption.count > 0,
                          }"
                          :title="`${bucketOption.count} tasks due today`"
                        >
                          <Icon icon="lucide:calendar-clock" class="sidenav-due-icon__icon" />
                          <span class="sidenav-due-icon__count">{{ bucketOption.count }}</span>
                        </span>
                        <span
                          v-if="bucketOption.unreadCount > 0"
                          class="sidenav-unread-icon-wrapper"
                          :title="`${bucketOption.unreadCount} unread message${bucketOption.unreadCount === 1 ? '' : 's'}`"
                        >
                          <Icon icon="mdi:comment-text" class="sidenav-unread-icon" />
                          <span class="sidenav-unread-count-badge">
                            {{ bucketOption.unreadCount > 99 ? "99+" : bucketOption.unreadCount }}
                          </span>
                        </span>
                      </span>
                    </li>
                  </router-link>
                </template>
              </draggable>
            </ul>
          </transition>
          <router-link :to="{ name: MEETINGS_ROUTE }">
            <li :class="[route === MEETINGS_ROUTE ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:calendar" width="20" height="20" />
                <span class="sidenav-label">Meetings</span>
                <span
                  v-if="dueMeetingsCount > 0"
                  class="sidenav-due-icon"
                  :class="{ glowing: dueMeetingsCount > 0, pulsing: dueMeetingsCount > 0 }"
                  :title="`${dueMeetingsCount} meeting${dueMeetingsCount === 1 ? '' : 's'} due today`"
                  @click.stop.prevent="goToDueMeetings"
                >
                  <Icon icon="lucide:calendar-clock" class="sidenav-due-icon__icon" />
                  <span class="sidenav-due-icon__count">{{ dueMeetingsCount }}</span>
                </span>
              </span>
            </li>
          </router-link>
          <router-link :to="{ name: OPPORTUNITIES_ROUTE }">
            <li :class="[isOpportunitiesRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:trophy" width="20" height="20" />
                <span class="sidenav-label">Opportunity</span>
              </span>
            </li>
          </router-link>
          <a @click="toggleReportsMenu" class="reports-menu-toggle">
            <li :class="[isReportsRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:bar-chart-3" width="20" height="20" />
                <span class="sidenav-label">Reports</span>
                <Icon
                  :icon="isReportsMenuOpen ? 'lucide:chevron-down' : 'lucide:chevron-right'"
                  class="menu-chevron"
                />
              </span>
            </li>
          </a>
          <transition name="slide">
            <ul v-if="isReportsMenuOpen && !isCollapsed" class="sub-menu">
              <router-link :to="{ name: ACTIVITY_REPORTS_ROUTE }">
                <li :class="[route === ACTIVITY_REPORTS_ROUTE ? 'selected' : null, 'nav-items']">
                  <span class="router-link">
                    <span class="sidenav-label">Activity Report</span>
                  </span>
                </li>
              </router-link>
              <router-link :to="{ name: REPORTS_ROUTE }">
                <li :class="[route === REPORTS_ROUTE ? 'selected' : null, 'nav-items']">
                  <span class="router-link">
                    <span class="sidenav-label">Metrics</span>
                  </span>
                </li>
              </router-link>
              <router-link :to="{ name: OPPORTUNITIES_REPORTS_ROUTE }">
                <li
                  :class="[route === OPPORTUNITIES_REPORTS_ROUTE ? 'selected' : null, 'nav-items']"
                >
                  <span class="router-link">
                    <span class="sidenav-label">Opportunity Report</span>
                  </span>
                </li>
              </router-link>
            </ul>
          </transition>
          <router-link :to="{ name: CALL_HISTORY_REPORTS_ROUTE }">
            <li :class="[route === CALL_HISTORY_REPORTS_ROUTE ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:phone" width="20" height="20" />
                <span class="sidenav-label">Call History</span>
              </span>
            </li>
          </router-link>
          <router-link :to="{ name: TEMPLATE_FOLDERS_ROUTE }">
            <li :class="[isTemplatesRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:file-text" width="20" height="20" />
                <span class="sidenav-label">Templates</span>
              </span>
            </li>
          </router-link>
          <router-link :to="{ name: SEQUENCES_ROUTE }">
            <li :class="[isSequencesRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:send" width="20" height="20" />
                <span class="sidenav-label">Sequences</span>
              </span>
            </li>
          </router-link>
          <router-link :to="{ name: SETTINGS_PROFILE_ROUTE }">
            <li :class="[isSettingsRouteActive ? 'selected' : null, 'nav-items']">
              <span class="router-link">
                <Icon icon="lucide:settings" width="20" height="20" />
                <span class="sidenav-label">Settings</span>
              </span>
            </li>
          </router-link>
        </div>
      </ul>
    </div>

    <div class="sidenav-footer">
      <button
        type="button"
        class="sidenav-toggle"
        :aria-label="isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'"
        :aria-expanded="!isCollapsed"
        @click="toggleCollapse"
      >
        <Icon
          :icon="isCollapsed ? 'lucide:panel-left-open' : 'lucide:panel-left-close'"
          width="16"
          height="16"
        />
      </button>
    </div>
  </div>

  <AddBucketOptionModal
    v-if="showAddBucketOptionModal"
    @close-add-bucket-option-modal="showAddBucketOptionModal = false"
    @refresh-bucket-options="refetchBucketOptions"
  />
</template>

<script setup>
// @ts-nocheck
import { ref, computed, watch, nextTick } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import debounce from "lodash/debounce";
import draggable from "vuedraggable/dist/vuedraggable.common.js";
import { Icon } from "@iconify/vue";

import AddBucketOptionModal from "@/components/bucket/options/AddBucketOptionModal.vue";
import { useBucketOptions, useBucketLockStatus } from "@/composables/bucket";
import { useDashboardStats } from "@/composables/dashboard/useDashboardStats";
import { useMeetingStats } from "@/composables/meetings";
import { useOrganizationSetting, useEditOrganizationSetting } from "@/composables/organization";
import { useSidenavCollapse } from "@/composables/ui/useSidenavCollapse";
import { usePermissions } from "@/composables/usePermissions";

import {
  ACTIVITY_REPORTS_ROUTE,
  BUCKETS_LIST_ROUTE,
  BUCKETS_ROUTE,
  CALL_HISTORY_REPORTS_ROUTE,
  COMPANIES_ROUTE,
  COMPANY_OVERVIEW_ROUTE,
  CONTACTS_ROUTE,
  CONTACT_IMPORT_ROUTE,
  CONTACT_OVERVIEW_ROUTE,
  CONVERT_CONTACT_OPPORTUNITY_ROUTE,
  DASHBOARD_ROUTE,
  MEETINGS_ROUTE,
  OPPORTUNITIES_REPORTS_ROUTE,
  OPPORTUNITIES_ROUTE,
  OPPORTUNITY_OVERVIEW_ROUTE,
  REPORTS_ROUTE,
  SETTINGS_ADMIN_ROUTE,
  SETTINGS_BILLING_ROUTE,
  SETTINGS_BUY_NEW_NUMBER_ROUTE,
  SETTINGS_CALL_RECORDINGS_ROUTE,
  SETTINGS_CALL_PHONE_NUMBERS_ROUTE,
  SETTINGS_DOWNLOADS_ROUTE,
  SETTINGS_GENERAL_ROUTE,
  SETTINGS_INTEGRATIONS_ROUTE,
  SETTINGS_PROFILE_ROUTE,
  TEMPLATES_ROUTE,
  TEMPLATE_FOLDERS_ROUTE,
  SEQUENCES_ROUTE,
  SEQUENCE_DETAIL_ROUTE,
} from "@/router/routes";

const vueRoute = useRoute();
const router = useRouter();
const store = useStore();

const { isCollapsed, isDrawerMode, toggle: toggleSidenav } = useSidenavCollapse();
const { canManageContacts } = usePermissions();

const CONTACTS_ROUTES = [
  CONTACTS_ROUTE,
  CONTACT_OVERVIEW_ROUTE,
  CONTACT_IMPORT_ROUTE,
  CONVERT_CONTACT_OPPORTUNITY_ROUTE,
];
const COMPANIES_ROUTES = [COMPANIES_ROUTE, COMPANY_OVERVIEW_ROUTE];
const BUCKETS_ROUTES = [BUCKETS_LIST_ROUTE, BUCKETS_ROUTE];
const OPPORTUNITIES_ROUTES = [OPPORTUNITIES_ROUTE, OPPORTUNITY_OVERVIEW_ROUTE];
const REPORTS_ROUTES = [ACTIVITY_REPORTS_ROUTE, REPORTS_ROUTE, OPPORTUNITIES_REPORTS_ROUTE];
const TEMPLATES_ROUTES = [TEMPLATE_FOLDERS_ROUTE, TEMPLATES_ROUTE];
const SEQUENCES_ROUTES = [SEQUENCES_ROUTE, SEQUENCE_DETAIL_ROUTE];
const SETTINGS_ROUTES = [
  SETTINGS_GENERAL_ROUTE,
  SETTINGS_PROFILE_ROUTE,
  SETTINGS_ADMIN_ROUTE,
  SETTINGS_BILLING_ROUTE,
  SETTINGS_INTEGRATIONS_ROUTE,
  SETTINGS_DOWNLOADS_ROUTE,
  SETTINGS_CALL_RECORDINGS_ROUTE,
  SETTINGS_CALL_PHONE_NUMBERS_ROUTE,
  SETTINGS_BUY_NEW_NUMBER_ROUTE,
];

const isBucketsMenuOpen = ref(false);
const isReportsMenuOpen = ref(false);
const showAddBucketOptionModal = ref(false);
const bucketOptionEditable = ref([]);
const totalBucketUnreadCount = computed(() =>
  bucketOptionEditable.value.reduce((sum, opt) => sum + (opt.unreadCount || 0), 0)
);
const showBucketsTooltip = ref(false);
const showReportsTooltip = ref(false);
const selectedBucketRef = ref(null);

const route = computed(() => vueRoute.name);
const routeParams = computed(() => vueRoute.params);
const account = computed(() => store.getters.account.data);

const isContactsRouteActive = computed(() => CONTACTS_ROUTES.includes(route.value));
const isCompaniesRouteActive = computed(() => COMPANIES_ROUTES.includes(route.value));
const isBucketsRouteActive = computed(() => BUCKETS_ROUTES.includes(route.value));
const isBucketsSubmenuExpanded = computed(
  () => (isBucketsMenuOpen.value || isBucketsRouteActive.value) && !isCollapsed.value
);
const isOpportunitiesRouteActive = computed(() => OPPORTUNITIES_ROUTES.includes(route.value));
const isReportsRouteActive = computed(() => REPORTS_ROUTES.includes(route.value));
const isTemplatesRouteActive = computed(() => TEMPLATES_ROUTES.includes(route.value));
const isSequencesRouteActive = computed(() => SEQUENCES_ROUTES.includes(route.value));
const isSettingsRouteActive = computed(() => SETTINGS_ROUTES.includes(route.value));

const bucketOptionsParams = computed(() => {
  if (!account.value?.organizationId) return null;
  return { organizationId: account.value.organizationId };
});

const dashboardParams = computed(() => {
  if (!account.value?.organizationId) return null;

  const now = new Date();
  const todayStartDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
  const todayEndDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
  const monday = new Date(now);
  const dayOfWeek = now.getDay();
  const diffToMonday = (dayOfWeek + 6) % 7;
  monday.setDate(now.getDate() - diffToMonday);
  monday.setHours(0, 0, 0, 0);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);

  const params = {
    todayStartDate,
    todayEndDate,
    offset: new Date().getTimezoneOffset(),
    startDate: monday.toISOString(),
    endDate: sunday.toISOString(),
    organizationId: account.value.organizationId,
  };

  if (!canManageContacts.value) {
    params.bucketOwnerId = account.value.id;
  }

  return params;
});

const { data: bucketOptions, refetch: refetchBucketOptions } =
  useBucketOptions(bucketOptionsParams);

const { data: dashboardStatsData } = useDashboardStats(dashboardParams);

const { data: organizationSetting } = useOrganizationSetting(bucketOptionsParams);

const editOrgSettingMutation = useEditOrganizationSetting();

// Bucket lock status
const bucketLockParams = computed(() => {
  if (!account.value?.organizationId) return null;
  return {
    organizationId: account.value.organizationId,
    offset: new Date().getTimezoneOffset(),
  };
});

const { data: bucketLockStatusData } = useBucketLockStatus(bucketLockParams);

const isBucketOptionLocked = (slug) => {
  if (!bucketLockStatusData.value?.enabled) return false;
  const status = bucketLockStatusData.value.bucketStatuses.find((s) => s.bucketOptionSlug === slug);
  return status?.isLocked || false;
};

// Due meetings count
const meetingStatsParams = computed(() => {
  if (!account.value?.organizationId) return null;
  const params = {
    organizationId: account.value.organizationId,
    offset: new Date().getTimezoneOffset(),
  };
  if (!canManageContacts.value) {
    params.contactOwnerId = account.value.id;
  }
  return params;
});

const { data: meetingStatsData } = useMeetingStats(meetingStatsParams);
const dueMeetingsCount = computed(() => meetingStatsData.value?.dueCount || 0);

const goToDueMeetings = () => {
  router.push({ name: MEETINGS_ROUTE, query: { schedule: "due" } });
};

const setBucketRef = (el, slug) => {
  if (routeParams.value.bucket !== slug) return;
  if (typeof el === "number") return;
  selectedBucketRef.value = el;
};

const toggleBucketsMenu = () => {
  if (isBucketsMenuOpen.value) {
    isBucketsMenuOpen.value = false;
  } else {
    if (isReportsMenuOpen.value) {
      isReportsMenuOpen.value = false;
      setTimeout(() => {
        isBucketsMenuOpen.value = true;
        router.push({ name: BUCKETS_LIST_ROUTE });
      }, 200);
    } else {
      isBucketsMenuOpen.value = true;
      router.push({ name: BUCKETS_LIST_ROUTE });
    }
  }
};

const toggleReportsMenu = () => {
  if (isReportsMenuOpen.value) {
    isReportsMenuOpen.value = false;
  } else {
    if (isBucketsMenuOpen.value) {
      isBucketsMenuOpen.value = false;
      setTimeout(() => {
        isReportsMenuOpen.value = true;
      }, 200);
    } else {
      isReportsMenuOpen.value = true;
    }
  }
};

const toggleCollapse = () => {
  toggleSidenav();
  if (isCollapsed.value) {
    isBucketsMenuOpen.value = false;
    isReportsMenuOpen.value = false;
  }
};

const sortBucketsByOrder = () => {
  if (!bucketOptions.value) return;

  const dueBySlug =
    dashboardStatsData.value?.bucketStatsByOption?.reduce((acc, stat) => {
      const slug = stat.bucketOption?.slug;
      if (slug != null) acc[slug] = stat.due ?? 0;
      return acc;
    }, {}) ?? {};

  const unreadBySlug =
    dashboardStatsData.value?.bucketStatsByOption?.reduce((acc, stat) => {
      const slug = stat.bucketOption?.slug;
      if (slug != null) acc[slug] = stat.unreadCount ?? 0;
      return acc;
    }, {}) ?? {};

  bucketOptionEditable.value = [...bucketOptions.value].map((opt) => ({
    ...opt,
    count: dueBySlug[opt.slug] ?? 0,
    unreadCount: unreadBySlug[opt.slug] ?? 0,
  }));

  if (organizationSetting.value?.bucketOrder) {
    const bucketOrder = organizationSetting.value.bucketOrder;
    bucketOptionEditable.value.sort((a, b) => {
      return bucketOrder.indexOf(a.slug) - bucketOrder.indexOf(b.slug);
    });
  }
};

const dragBucketOption = () => {
  if (!organizationSetting.value) return;

  const newSetting = {
    ...organizationSetting.value,
    bucketOrder: bucketOptionEditable.value.map((e) => e.slug),
  };

  editOrgSettingMutation.mutate(newSetting);
};

const debouncedDragBucketOption = debounce(dragBucketOption, 200);

const scrollToSelectedBucket = () => {
  nextTick(() => {
    selectedBucketRef.value?.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  });
};

watch(
  route,
  () => {
    isBucketsMenuOpen.value =
      isBucketsRouteActive.value &&
      (route.value === BUCKETS_LIST_ROUTE ||
        bucketOptions.value?.map((e) => e.slug).includes(routeParams.value.bucket));

    isReportsMenuOpen.value = isReportsRouteActive.value;
  },
  { immediate: true }
);

watch(
  () => routeParams.value.bucket,
  () => {
    isBucketsMenuOpen.value =
      isBucketsRouteActive.value &&
      (route.value === BUCKETS_LIST_ROUTE ||
        bucketOptions.value?.map((e) => e.slug).includes(routeParams.value.bucket));

    scrollToSelectedBucket();
  },
  { immediate: true }
);

watch(
  [bucketOptions, organizationSetting, dashboardStatsData],
  () => {
    sortBucketsByOrder();
  },
  { immediate: true }
);
</script>

<style scoped>
.sidenav {
  padding-right: 0px;
  background-color: #050559;
  height: 100vh;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.sidenav .links {
  flex: 1;
  min-height: 0;
}

.sidenav-footer {
  position: sticky;
  bottom: 0;
  padding: 10px;
  background: #050559;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  justify-content: flex-start;
  margin-top: auto;
}

.sidenav-collapsed .sidenav-footer {
  justify-content: center;
  padding: 10px 8px;
}

.sidenav-toggle {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0;
  transition:
    background-color 120ms ease,
    color 120ms ease;
}

.sidenav-toggle:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.sidenav-toggle:focus-visible {
  outline: 2px solid #7c7cff;
  outline-offset: 2px;
}

.sidenav.sidenav-collapsed {
  overflow: visible;
}

.sidenav .logo-img {
  width: 100%;
  height: auto;
  max-width: 180px;
  max-height: 32px;
  margin: 0 auto;
  display: block;
  object-fit: contain;
}

.logo-link {
  text-decoration: none;
  display: block;
  cursor: pointer;
}

.logo-container {
  background: #ffffff;
  border-radius: 24px;
  padding: 6px 16px;
  margin: 0 auto 15px auto;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 220px;
  max-width: 220px;
  min-width: 220px;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.logo-container:hover {
  transform: scale(1.02);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.sidenav ul {
  list-style: none;
  width: 100%;
  margin-block-start: 0px;
  padding-inline-start: 0px;
}

.sidenav ul a:hover {
  text-decoration: unset;
}

.sidenav ul .nav-items {
  width: 90%;
  margin-left: 2.5%;
  font-weight: 300 !important;
  font-size: 0.85rem;
  line-height: 1.4rem;
  letter-spacing: 0.25px;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.85);
  border-radius: 6px !important;
}

.sidenav ul .sub-menu-icon {
  margin-right: 10px;
  font-size: 0.85rem;
}

.sub-menu-icon {
  font-size: 0.7rem !important;
  margin-right: 8px !important;
  position: relative;
  bottom: 2px;
}

.sub-menu-text {
  color: rgba(255, 255, 255, 0.85) !important;
  font-weight: 300 !important;
}

.sidenav ul .selected {
  font-weight: 300 !important;
  font-size: 0.85rem;
  line-height: 1.4rem;
  padding-top: 0.4rem;
  padding-bottom: 0.4rem;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.95);
  background-color: #5c59e8;
  border-radius: 6px !important;
}

.sidenav ul .nav-items:hover {
  background-color: #5c59e8;
}

.sidenav ul .selected:hover {
  background-color: #5c59e8;
}

.sidenav ul .router-link {
  display: flex;
  align-items: center;
}

.sidenav .router-link img,
.sidenav .router-link .iconify {
  height: 0.95rem;
  width: 0.95rem;
  margin-right: 12px;
  color: rgba(255, 255, 255, 0.85);
  opacity: 0.9;
}

.buckets-row-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.add-bucket-icon {
  height: 1.1rem;
  cursor: pointer;
}

.buckets-chevron {
  margin-left: 0 !important;
}

.buckets-menu-toggle,
.reports-menu-toggle {
  cursor: pointer;
}

.menu-chevron {
  margin-left: auto;
  height: 1.1rem;
  margin-right: 10px;
  font-size: 0.7rem;
}

.slide-enter-active,
.slide-leave-active {
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    transform 0.35s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-enter,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
  transform: translateY(-8px);
  overflow: hidden;
}

.slide-enter-to,
.slide-leave {
  max-height: 200px;
  opacity: 1;
  transform: translateY(0);
  overflow: hidden;
}

.scrollable-sub-menu {
  max-height: 200px;
  overflow-y: auto;
  margin-top: 6px;
}

.sidenav::-webkit-scrollbar,
.sub-menu::-webkit-scrollbar {
  width: 3px;
}

.sub-menu::-webkit-scrollbar-track,
.sidenav::-webkit-scrollbar-track {
  background: transparent;
}

.sub-menu::-webkit-scrollbar-thumb,
.sidenav::-webkit-scrollbar-thumb {
  background-color: #413fb9ff;
  border-radius: 3px;
}

.sub-menu::-webkit-scrollbar-thumb:hover,
.sidenav::-webkit-scrollbar-thumb:hover {
  background-color: #413fb9ff;
}

.sub-menu,
.sidenav {
  scrollbar-width: thin;
  scrollbar-color: #413fb9ff transparent;
}

.sub-menu {
  width: 100%;
  box-sizing: border-box;
  padding-left: 0;
  margin-left: 0;
}

.sub-menu .nav-items {
  padding-top: 3px !important;
  padding-bottom: 3px !important;
  width: 100%;
  box-sizing: border-box;
  overflow: hidden;
}

.sub-menu .nav-items .router-link {
  max-width: 100%;
  overflow: hidden;
  padding-left: 20px;
  font-size: 0.8rem;
  width: 100%;
  box-sizing: border-box;
}

.scrollable-sub-menu .nav-items .router-link {
  padding-left: 10px;
}

.sub-menu .nav-items .sidenav-label {
  max-width: 140px;
  display: inline-block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
  flex: 1;
  min-width: 0;
}

.sidenav-due-icon {
  margin-left: auto;
  margin-right: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  height: 22px;
  min-width: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background-color: #059669;
  color: #ffffff;
  border: 1px solid rgba(255, 255, 255, 0.25);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex-shrink: 0;
  cursor: pointer;
}

.sidenav-due-icon.glowing {
  box-shadow:
    0 0 12px rgba(5, 150, 105, 0.5),
    0 0 20px rgba(5, 150, 105, 0.35);
  transform: scale(1.05);
}

.sidenav-due-icon.pulsing {
  animation: sidenavPulseScale 2s ease-in-out infinite;
}

@keyframes sidenavPulseScale {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.08);
  }
  100% {
    transform: scale(1);
  }
}

.sidenav-due-icon__icon {
  font-size: 14px;
  color: inherit;
}

.sidenav-due-icon__count {
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1;
  color: inherit;
}

.tooltip-item .sidenav-due-icon {
  margin-right: 0;
}

.sub-menu .sidenav-due-icon {
  margin-right: 10px;
}

.sidenav-lock-icon {
  margin-left: auto;
  margin-right: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  flex-shrink: 0;
}

.tooltip-item .sidenav-lock-icon {
  margin-right: 0;
  color: #6b7280;
  font-size: 13px;
}

.dragging {
  opacity: 0.5;
  background: #eee;
}

.drag-handle {
  cursor: grab;
}

.tooltip-drag-handle {
  cursor: grab;
  transition: opacity 0.2s ease;
}

.tooltip-drag-handle:hover {
  opacity: 1 !important;
}

.tooltip-dragging {
  opacity: 0.5;
  background: #f3f4f6;
  border-radius: 8px;
  cursor: grabbing;
}

.sidenav:not(.sidenav-drawer):not(.sidenav-collapsed) {
  padding-left: 15px;
}

.sidenav:not(.sidenav-drawer) ul .nav-items {
  padding-left: 15px;
}

.sidenav-drawer {
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 1050;
  transition:
    width 280ms cubic-bezier(0.16, 1, 0.3, 1),
    transform 280ms cubic-bezier(0.16, 1, 0.3, 1);
  background-color: #050559;
  width: 260px !important;
}

.sidenav-drawer.sidenav-collapsed {
  width: 56px !important;
  transform: translateX(0);
}

.sidenav-drawer:not(.sidenav-collapsed) {
  width: 260px !important;
  max-width: 260px;
  transform: translateX(0);
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
  padding-left: 15px;
}

.sidenav-drawer:not(.sidenav-collapsed) .nav-items {
  width: 92%;
  margin-left: 0;
}

.collapsed-logo-top {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 15px 0;
  margin-top: 5px;
  width: 100%;
}

.logo-icon-small {
  width: 24px;
  height: 24px;
}

.collapsed-nav-icons {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  padding: 5px 0;
  margin-top: 5px;
  width: 100%;
}

.collapsed-nav-link {
  text-decoration: none;
  display: block;
}

.collapsed-nav-item {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 6px;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
}

.collapsed-nav-item:hover {
  background-color: rgba(92, 89, 232, 0.2);
}

.collapsed-nav-item.selected {
  background-color: #5c59e8;
}

.collapsed-nav-item img,
.collapsed-nav-item .iconify {
  width: 17px;
  height: 17px;
  color: #ffffff;
}

.collapsed-nav-item-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
}

.nav-tooltip {
  position: absolute;
  left: 100%;
  top: 0;
  padding-left: 12px;
  z-index: 10001;
  pointer-events: none;
  opacity: 0;
  visibility: hidden;
  transform: translateX(-8px);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tooltip-content {
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  box-shadow:
    0 8px 24px rgba(17, 24, 39, 0.12),
    0 2px 4px rgba(17, 24, 39, 0.05);
  min-width: 200px;
  max-width: 260px;
  overflow: hidden;
  position: relative;
}

.tooltip-footer-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  padding: 9px 14px;
  border: none;
  border-top: 1px solid #f0f1f5;
  background: transparent;
  color: #3e3efe;
  font-size: 0.78rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 120ms ease;
}

.tooltip-footer-action:hover {
  background: rgba(62, 62, 254, 0.06);
}

.tooltip-footer-action:focus-visible {
  outline: 2px solid rgba(62, 62, 254, 0.4);
  outline-offset: -2px;
}

.tooltip-items {
  max-height: 280px;
  overflow-y: auto;
  padding: 6px;
}

.tooltip-items::-webkit-scrollbar {
  width: 4px;
}

.tooltip-items::-webkit-scrollbar-track {
  background: transparent;
}

.tooltip-items::-webkit-scrollbar-thumb {
  background: rgba(62, 62, 254, 0.25);
  border-radius: 2px;
}

.tooltip-items::-webkit-scrollbar-thumb:hover {
  background: rgba(62, 62, 254, 0.45);
}

.tooltip-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  color: #374151;
  text-decoration: none;
  font-size: 0.8rem;
  font-weight: 500;
  border-radius: 6px;
  position: relative;
  cursor: pointer;
  transition:
    background-color 120ms ease,
    color 120ms ease;
}

.tooltip-item .tooltip-drag-handle {
  opacity: 0.5;
  font-size: 0.95rem;
  cursor: grab;
}

.tooltip-item .tooltip-drag-handle:active {
  cursor: grabbing;
}

.tooltip-item__bucket-icon {
  font-size: 0.85rem;
}

.tooltip-item + .tooltip-item {
  margin-top: 2px;
}

.tooltip-empty {
  padding: 12px 10px;
  color: #9ca3af;
  font-size: 0.78rem;
  text-align: center;
}

.tooltip-item:hover {
  background: rgba(62, 62, 254, 0.08);
  color: #111827;
  text-decoration: none;
}

.tooltip-item-selected {
  background: rgba(62, 62, 254, 0.1);
  color: #3e3efe;
  font-weight: 600;
}

.tooltip-item .tooltip-icon {
  color: #6b7280;
  flex-shrink: 0;
  transition: color 120ms ease;
}

.tooltip-item:hover .tooltip-icon {
  color: #3e3efe;
}

.tooltip-item-selected .tooltip-icon {
  color: #3e3efe;
}

.tooltip-count {
  margin-left: auto;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  border-radius: 12px;
  min-width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
  transition: all 0.2s ease;
}

.tooltip-item:hover .tooltip-count {
  transform: scale(1.05);
  box-shadow: 0 4px 8px rgba(16, 185, 129, 0.4);
}

.nav-tooltip--label {
  pointer-events: none;
}

.tooltip-content--label {
  min-width: auto;
  max-width: none;
  white-space: nowrap;
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 500;
  color: #374151;
  border-radius: 8px;
  background-color: #ffffff;
  border: 1px solid #e1e5e9;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tooltip-content--label::before,
.tooltip-content--label::after {
  display: none;
}

.collapsed-nav-item-container:hover .collapsed-nav-item {
  box-shadow: 0 0 0 2px rgba(92, 89, 232, 0.3);
  transform: scale(1.05);
}

.collapsed-nav-item-container:hover .nav-tooltip {
  pointer-events: auto;
  opacity: 1;
  visibility: visible;
  transform: translateX(0);
}

.collapsed-nav-item-container:hover .nav-tooltip .tooltip-item {
  pointer-events: auto;
}

.sidenav-collapsed .collapsed-nav-icons {
  overflow: visible;
}

.sidenav-drawer:not(.sidenav-collapsed) .nav-items {
  padding-left: 12px;
}

.sidenav-drawer:not(.sidenav-collapsed) .sub-menu .nav-items {
  padding-left: 0;
  padding-top: 2px !important;
  padding-bottom: 2px !important;
}

.sidenav-drawer:not(.sidenav-collapsed) .scrollable-sub-menu {
  margin-top: 4px;
  max-height: 186px;
}

.sidenav-drawer.sidenav-collapsed .logo-container {
  display: none;
}

.sidenav-drawer:not(.sidenav-collapsed) .logo-img {
  max-width: 140px;
  max-height: 28px;
}

.sidenav-drawer > .links,
.sidenav-drawer > .collapsed-nav-icons,
.sidenav-drawer > .collapsed-logo-top {
  transition: opacity 0.3s ease;
}

.sidenav-drawer.sidenav-collapsed::-webkit-scrollbar {
  display: none;
}

.sidenav-drawer.sidenav-collapsed {
  scrollbar-width: none;
}

.sidenav-drawer:not(.sidenav-collapsed) .scrollable-sub-menu::-webkit-scrollbar {
  display: block;
  width: 3px;
}

.sidenav-drawer:not(.sidenav-collapsed) .scrollable-sub-menu {
  scrollbar-width: thin;
  scrollbar-color: #413fb9ff transparent;
}

@media (max-width: 767px) {
  .sidenav-drawer {
    display: none;
  }
}

.sidenav-unread-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background-color: #ef4444;
  color: #ffffff;
  border-radius: 10px;
  font-size: 0.65rem;
  font-weight: 600;
  margin-left: auto;
  margin-right: 8px;
  line-height: 1;
}

.sidenav-unread-badge--collapsed {
  position: absolute;
  top: -4px;
  right: -4px;
  margin: 0;
  min-width: 18px;
  height: 18px;
  font-size: 0.6rem;
  padding: 0 4px;
}

.sidenav-unread-icon-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 24px;
  margin-left: auto;
  margin-right: 8px;
  margin-top: 4px;
  flex-shrink: 0;
}

/* Overrides the generic ".sidenav .router-link .iconify" rule (color/size/margin/opacity
   for all nav icons), which otherwise wins on specificity and distorts this one. */
.sidenav .router-link .sidenav-unread-icon-wrapper .sidenav-unread-icon.iconify {
  width: 18px;
  height: 18px;
  font-size: 18px;
  margin-right: 0;
  color: #22c55e;
  opacity: 1;
}

.sidenav-unread-count-badge {
  position: absolute;
  top: -3px;
  right: -3px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  min-width: 15px;
  height: 15px;
  padding: 0 4px;
  border-radius: 999px;
  border: 1.5px solid #050559;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
</style>
